import React, { useEffect, lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import './App.css';
import './index.css';

import Home from './pages/Home';
import Services from './pages/Services';
import Doctors from './pages/Doctors';
import DiagnosticCentre from './pages/DiagnosticCentre';
import BloodBank from './pages/BloodBank';
import Gallery from './pages/Gallery';
import Testimonials from './pages/Testimonials';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import Jobs from './pages/Jobs';
// Admin/Master/HR/Marketing dashboards are heavy; code-split so they don't bloat the
// public bundle. They're only requested when an admin actually navigates to /admin etc.
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const MarketingDashboard = lazy(() => import('./pages/marketing/MarketingDashboard'));
const HumanResourcesDashboard = lazy(() => import('./pages/humanresources/HumanResourcesDashboard'));
const MasterLogin = lazy(() => import('./pages/master/MasterLogin'));
const MasterDashboard = lazy(() => import('./pages/master/MasterDashboard'));
const ChatViewerLogin = lazy(() => import('./pages/chatviewer/ChatViewerLogin'));
const ChatViewerDashboard = lazy(() => import('./pages/chatviewer/ChatViewerDashboard'));
import DynamicPage from './pages/DynamicPage';
import WhatsAppButton from './components/WhatsAppButton';
import NewsletterPopup from './components/NewsletterPopup';
// Defer the floating chat widget so it doesn't compete with first paint on the Home hero.
const AIChatWidget = lazy(() => import('./components/AIChatWidget'));

// Mounts AIChatWidget only after the page has been idle / rendered for ~1s, so it never
// competes with the hero image/video for first paint.
const DeferredChatWidget = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = idle(() => setShow(true), { timeout: 2000 });
    return () => cancel(handle);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <AIChatWidget />
    </Suspense>
  );
};

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <div className="App">
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-neutral-400">Loading...</div>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/doctors" element={<Doctors />} />
                  <Route path="/diagnostic-centre" element={<DiagnosticCentre />} />
                  <Route path="/blood-bank" element={<BloodBank />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/jobs" element={<Jobs />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                  <Route path="/marketing/*" element={<MarketingDashboard />} />
                  <Route path="/humanresources/*" element={<HumanResourcesDashboard />} />
                  {/* Master Panel Routes */}
                  <Route path="/master/login" element={<MasterLogin />} />
                  <Route path="/master/*" element={<MasterDashboard />} />
                  {/* Chatbot Viewer Routes */}
                  <Route path="/chatbot/login" element={<ChatViewerLogin />} />
                  <Route path="/chatbot/dashboard" element={<ChatViewerDashboard />} />
                  {/* Legacy HR route - redirect to marketing */}
                  <Route path="/hr/*" element={<MarketingDashboard />} />
                  {/* Dynamic pages - catch-all for custom pages created via admin */}
                  <Route path="/*" element={<DynamicPage />} />
                </Routes>
              </Suspense>
              <WhatsAppButton />
              <DeferredChatWidget />
              <NewsletterPopup />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;